/**
 * MobileDockTriggerStrip — narrow-viewport relocation of the
 * UniversalDock's three primary buttons (Report / Teams / AI Chat).
 *
 * Why this component exists
 * =========================
 * The floating bottom-right ``UniversalDock`` ate ~50 px of bottom
 * viewport on phones and overlapped the email reading pane (operator
 * complaint 2026-05-24: "in mobile view the teams bug ai make it go
 * in the top nav.. as that space is empty"). On the existing
 * Newmark / SAM header layout the right cluster has the bell + avatar
 * jammed against the right edge with several empty pixels to the LEFT
 * of the org switcher — that's where these three icon buttons fit
 * naturally without crowding any other affordance.
 *
 * Design
 * ======
 * - Three icon-only buttons matching the dock's three built-in
 *   panels (Bug / Teams / AI). Labels are exposed via ``aria-label``
 *   + ``title`` for accessibility + native tooltips; no visual text
 *   so each button is 28 px wide (matches the bell + avatar pill).
 * - Self-gated to viewports below lg via ``flex lg:hidden`` so the
 *   floating dock and this strip never show at the same time. The
 *   bottom navigation uses that same lg gate.
 * - On click, dispatches a ``CustomEvent`` on ``window`` so the
 *   ``UniversalDock`` (which owns the panel state machine + the
 *   panel render slots) can toggle the requested panel. We use an
 *   event bus rather than lifting state because:
 *     1. The dock is rendered at the shell root via a portal; the
 *        Header is a sibling, not an ancestor. Wiring a shared
 *        Context across the AppShell would require threading
 *        every tenant's shell file — three repos + per-tenant
 *        forks of the header. The event bus is one file in the
 *        UI package + a 4-line ``addEventListener`` in the dock.
 *     2. Panel state should NOT be shareable across viewports
 *        anyway — a panel opened from the desktop bar should
 *        not stay open after the operator resizes to phone width
 *        (the close affordance moves with the dock). The event
 *        bus model keeps the open/close transition local to the
 *        viewport that originated it.
 *
 * Event contract
 * ==============
 * - Event name: ``quotech-dock-toggle-panel``
 * - ``detail.panelId``: ``'bug' | 'teams' | 'ai'`` — matches the
 *   built-in panel ids documented in ``getBuiltinPanels`` (in
 *   ``UniversalDock.tsx``).
 * - Dispatched on ``window`` so the listener inside the dock can
 *   ``window.addEventListener`` without any DOM-traversal coupling.
 *
 * @path packages/ui/src/components/universal-dock/MobileDockTriggerStrip.tsx
 */

import { Bug, Sparkles, Users } from 'lucide-react'

/** Shared event name. Exported so the dock listener uses the SAME
 *  string. */
export const DOCK_TOGGLE_PANEL_EVENT = 'quotech-dock-toggle-panel'

/** Built-in panel ids the trigger strip can address. Mirrors the
 *  ids in ``getBuiltinPanels`` inside ``UniversalDock.tsx``. */
export type DockPanelId = 'bug' | 'teams' | 'ai'

export interface DockTogglePanelEventDetail {
  panelId: DockPanelId
}

/** Type-safe dispatcher so consumers don't have to remember the
 *  event name or detail shape. */
export function dispatchDockTogglePanel(panelId: DockPanelId): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<DockTogglePanelEventDetail>(DOCK_TOGGLE_PANEL_EVENT, {
      detail: { panelId },
    }),
  )
}

interface TriggerButtonProps {
  panelId: DockPanelId
  label: string
  icon: React.ReactNode
}

function TriggerButton({ panelId, label, icon }: TriggerButtonProps) {
  return (
    <button
      type="button"
      onClick={() => dispatchDockTogglePanel(panelId)}
      // 2026-05-24 follow-up (operator: "the fuck is with this nav bar
      // in mobile view here?"): the original `p-1.5` padding wrapped a
      // 16 px icon into a 28×28 px hit-target — well below the iOS 44×
      // 44 / Android 48×48 minimums. On a 360 px viewport the three
      // triggers + search icon + notification + avatar visually
      // collapsed into a single tiny smudge against the right edge,
      // reading as "decorative chrome" not "tappable". Bump to `p-2`
      // for a 32×32 visual + a wider tap area (still compact enough
      // that three buttons + the bell + avatar fit on a 360 px row
      // without wrapping). Real-world hit-area still benefits from
      // ``min-h-[36px] min-w-[36px]`` because mobile browsers extend
      // the touch region beyond visual padding.
      className="inline-flex min-h-[36px] min-w-[36px] items-center justify-center p-2 text-muted-foreground hover:text-foreground rounded-md hover:bg-accent transition-colors"
      aria-label={label}
      title={label}
    >
      {icon}
    </button>
  )
}

/**
 * Narrow-only icon strip. Renders nothing at lg and above because the
 * floating ``UniversalDock`` remains the canonical surface on
 * desktop. Mount this inside the master ``Header`` right cluster,
 * ideally just before the ``NotificationBell`` so the bug → teams
 * → AI → bell → avatar reading order matches the desktop bar.
 */
export function MobileDockTriggerStrip() {
  return (
    <div
      className="flex lg:hidden items-center gap-0.5"
      data-quotech-mobile-dock-triggers
    >
      <TriggerButton
        panelId="bug"
        label="Report a bug"
        icon={<Bug className="w-4 h-4" />}
      />
      <TriggerButton
        panelId="teams"
        label="Teams chat"
        icon={<Users className="w-4 h-4" />}
      />
      <TriggerButton
        panelId="ai"
        label="AI Chat"
        icon={<Sparkles className="w-4 h-4" />}
      />
    </div>
  )
}

export default MobileDockTriggerStrip
